import addYears from 'date-fns/addYears'

import {
  CustomFieldSectionProjection,
  HomeDataDistributionCategory,
  OnlineTravelAgency,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatDate, pluralize } from '@avantstay/backoffice-core'

import {
  customFieldsSectionIdList,
  propertyListingAndDistributionCustomFieldsSections,
} from '@new/domains/properties/constants'
import {
  DistributionChannelsSubSection,
  DistributionNavEnum,
  OtaSyncOptions,
  SyncDistributionParams,
} from '@new/domains/properties/screens/propertyDistributionChannels/DistributionChannels.types'
import { otaSyncOptionsByChannel } from '@new/domains/properties/screens/propertyDistributionChannels/OtaDistributionChannel/components/SyncSidePanel/SyncSidePanel.constants'
import {
  HomeDistributionIssue,
  HomeDistributions,
} from '@new/domains/properties/screens/viewMode/ViewMode.types'

import {
  HomesDistributions,
  HomesDistributionsAndListings,
  HomesListings,
} from '@arriere/types/Properties.types'
import { formatOTAName } from '@utils/formatSectionName'

export const buildSyncDistributionParams = (
  syncOptions: OtaSyncOptions[],
): SyncDistributionParams => ({
  period: {
    start: formatDate(new Date()),
    end: formatDate(addYears(new Date(), 2)),
  },
  pushAmenities: syncOptions.includes(OtaSyncOptions.amenities),
  pushCalendar: syncOptions.includes(OtaSyncOptions.availability),
  pushContent: syncOptions.includes(OtaSyncOptions.listingContent),
  pushHomePhotos: syncOptions.includes(OtaSyncOptions.photos),
  pushPricing: syncOptions.includes(OtaSyncOptions.pricing),
  pushRooms: syncOptions.includes(OtaSyncOptions.rooms),
  pushSeasonalDiscounts: syncOptions.includes(OtaSyncOptions.seasonalDiscounts),
  pushTaxFees: syncOptions.includes(OtaSyncOptions.feesAndTaxes),
})

export const syncOptionByCategory: Record<string, OtaSyncOptions> = {
  [HomeDataDistributionCategory.availability]: OtaSyncOptions.availability,
  [HomeDataDistributionCategory.listingInfo]: OtaSyncOptions.listingContent,
  [HomeDataDistributionCategory.listingAmenity]: OtaSyncOptions.amenities,
  [HomeDataDistributionCategory.listingImage]: OtaSyncOptions.photos,
  [HomeDataDistributionCategory.listingRoom]: OtaSyncOptions.rooms,
  [HomeDataDistributionCategory.pricingRates]: OtaSyncOptions.pricing,
  [HomeDataDistributionCategory.pricingTaxesAndFees]: OtaSyncOptions.feesAndTaxes,
  [HomeDataDistributionCategory.pricingSeasonalDiscounts]: OtaSyncOptions.seasonalDiscounts,
}

const otaToDistributionNav: Partial<Record<string, string>> = {
  [OnlineTravelAgency.airbnb]: DistributionNavEnum.airbnb,
  [OnlineTravelAgency.rentalsUnited]: DistributionNavEnum.rentalsUnited,
  [OnlineTravelAgency.vrbo]: DistributionNavEnum.vrbo,
  [OnlineTravelAgency.yieldPlanet]: DistributionNavEnum.yieldPlanet,
}

export function isSyncOptionSupportedByOta(
  ota: OnlineTravelAgency | DistributionNavEnum | string | undefined,
  syncOption: OtaSyncOptions | HomeDataDistributionCategory,
): boolean {
  const normalizedOta = otaToDistributionNav[ota as string] ?? ota
  const channelOptions = otaSyncOptionsByChannel[normalizedOta as keyof typeof otaSyncOptionsByChannel]
  if (!channelOptions) return true

  const optionToCheck = syncOptionByCategory[syncOption] ?? syncOption
  return channelOptions.some(opt => opt.value === optionToCheck)
}

export const PETS_CUSTOM_FIELD_ID = 'b0d57417-4141-11ea-b6d1-e9276cc1d34f'
export const GUEST_RESTRICTIONS_CUSTOM_FIELD_ID = 'e95b937a-fe83-4407-a70e-c032d715d283'

export const DISTRIBUTION_CHANNELS = [
  OnlineTravelAgency.airbnb,
  OnlineTravelAgency.rentalsUnited,
  OnlineTravelAgency.vrbo,
]

export const HOTEL_DISTRIBUTION_CHANNELS = [
  ...DISTRIBUTION_CHANNELS,
  OnlineTravelAgency.yieldPlanet,
]

export const DISTRIBUTION_SUB_SECTIONS = {
  AIRBNB: [
    {
      label: 'Listing Description',
      subSection: DistributionChannelsSubSection.listingDescription,
    },
    {
      label: 'Listing Info',
      subSection: DistributionChannelsSubSection.listingInfo,
    },
    {
      label: 'Listing Locked Attributes',
      subSection: DistributionChannelsSubSection.listingLockedAttributes,
    },
    {
      label: 'Property Details',
      subSection: DistributionChannelsSubSection.propertyDetails,
    },
    {
      label: 'Guest Controls',
      subSection: DistributionChannelsSubSection.guestControls,
    },
    {
      label: 'Trip Issues',
      subSection: DistributionChannelsSubSection.tripIssues,
    },
  ],
  RENTALS_UNITED: [
    {
      label: 'Listing Description',
      subSection: DistributionChannelsSubSection.listingDescription,
    },
  ],
  VRBO: [
    {
      label: 'Listing Description',
      subSection: DistributionChannelsSubSection.listingDescription,
    },
  ],
  YIELD_PLANET: [],
}

interface GetSortedDistributionChannelsProps {
  distributionChannels: OnlineTravelAgency[]
  homeDistributionsAndListings?: HomesDistributionsAndListings
}

export interface DistributionChannelProps {
  currentDistribution?: HomesDistributions[0]
  currentListing?: HomesListings[0]
  label: string
  ota: OnlineTravelAgency
}

export const getSortedDistributionChannels = ({
  distributionChannels,
  homeDistributionsAndListings,
}: GetSortedDistributionChannelsProps): DistributionChannelProps[] => {
  const formattedDistributionChannels = distributionChannels.reduce(
    (
      accDistributionChannel: DistributionChannelProps[],
      currentDistributionChannel: OnlineTravelAgency,
    ) => {
      const currentDistribution = homeDistributionsAndListings?.distributions?.find(
        homeDistribution => homeDistribution.agencyAccount.agency === currentDistributionChannel,
      )

      const currentListing = homeDistributionsAndListings?.listings?.find(
        homeListing => homeListing.agencyAccount?.agency === currentDistributionChannel,
      )

      return [
        ...accDistributionChannel,
        {
          currentDistribution,
          currentListing,
          label: formatOTAName(currentDistributionChannel),
          ota: currentDistributionChannel,
        },
      ]
    },
    [],
  )

  const sortedDistributionChannels = formattedDistributionChannels.sort(
    (distributionChannelA, distributionChannelB) => {
      if (
        distributionChannelA.currentListing?.active &&
        distributionChannelB.currentListing?.active
      ) {
        return 0
      }

      if (distributionChannelA.currentListing?.active) {
        return -1
      }

      return 1
    },
  )

  return sortedDistributionChannels
}

export const listingAndDistributionSectionsIds =
  propertyListingAndDistributionCustomFieldsSections.map(
    section => customFieldsSectionIdList[section],
  )

export const onlyListingAndDistributionSections = <T extends CustomFieldSectionProjection>(
  customFields: T,
) =>
  [
    ...listingAndDistributionSectionsIds,
    PETS_CUSTOM_FIELD_ID,
    GUEST_RESTRICTIONS_CUSTOM_FIELD_ID,
  ].includes(customFields?.section.id)

interface GetTooltipTextProps {
  errorCount: number
  warningCount: number
}

interface GetDistributionChannelsWithIssueProps {
  currentDistribution: HomeDistributions
}

type IssueProps = HomeDistributions['statuses'][0]

export interface CurrentIssuesProps {
  errors: HomeDistributionIssue[]
  warnings: HomeDistributionIssue[]
}

export const getTooltipText = ({ errorCount, warningCount }: GetTooltipTextProps): string => {
  const errorText = pluralize(errorCount, 'errors', 'error')
  const warningText = pluralize(warningCount, 'warnings', 'warning')

  switch (true) {
    case errorCount > 0 && warningCount > 0:
      return `${errorCount} listing ${errorText} • ${warningCount} listing ${warningText}`
    case errorCount > 0:
      return `${errorCount} listing ${errorText}`
    case warningCount > 0:
      return `${warningCount} listing ${warningText}`
    default:
      return ''
  }
}

export const getDistributionChannelsWithIssue = ({
  currentDistribution,
}: GetDistributionChannelsWithIssueProps): CurrentIssuesProps => {
  return currentDistribution.statuses.reduce(
    (allIssues: CurrentIssuesProps, currentIssue: IssueProps) => {
      const { status } = currentIssue
      const currentErrors = status.errors || []
      const currentWarnings = status.warnings || []

      return {
        errors: [...allIssues.errors, ...currentErrors],
        warnings: [...allIssues.warnings, ...currentWarnings],
      }
    },
    { errors: [], warnings: [] },
  )
}
